import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import LostGuide from "@/pages/LostGuide";
import Dashboard from "@/pages/Dashboard";
import DoudouNew from "@/pages/DoudouNew";
import DoudouDetail from "@/pages/DoudouDetail";
import DoudouEdit from "@/pages/DoudouEdit";
import ScanPage from "@/pages/ScanPage";
import FoundPage from "@/pages/FoundPage";
import OrderStickers from "@/pages/OrderStickers";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Chat from "@/pages/Chat";
import Profile from "@/pages/Profile";
import AuthCallback from "@/pages/AuthCallback";
import RelaisPage from "@/pages/RelaisPage";
import Conversations from "@/pages/Conversations";
import CGU from "@/pages/CGU";
import Privacy from "@/pages/Privacy";
import MentionsLegales from "@/pages/MentionsLegales";
import AdminDashboard from "@/pages/AdminDashboard";
import FAQ from "@/pages/FAQ";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import FoundReports from "@/pages/FoundReports";
import FinderHub from "@/pages/FinderHub";
import FinderConversationRedirect from "@/pages/FinderConversationRedirect";
import AlertePublique from "@/pages/AlertePublique";
import ChangePassword from "@/pages/ChangePassword";
import Desabonnement from "@/pages/Desabonnement";
import CookieBanner from "@/components/CookieBanner";
import ScrollToTop from "@/components/ScrollToTop";

/**
 * Force-redirect users with must_change_password to the change password page,
 * except when already on that page or on legal/login pages.
 */
function PasswordGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const allowed = ["/changer-mot-de-passe", "/cgu", "/confidentialite", "/mentions-legales"];
  if (user?.must_change_password && !allowed.includes(location.pathname)) {
    return <Navigate to="/changer-mot-de-passe" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent Google Auth callback (fragment-based session_id)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <PasswordGate>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/connexion" element={<Login />} />
      <Route path="/inscription" element={<Register />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
      <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
      <Route path="/j-ai-perdu-un-doudou" element={<LostGuide />} />
      <Route path="/changer-mot-de-passe" element={<ChangePassword />} />
      <Route path="/desabonnement" element={<Desabonnement />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/doudou/nouveau" element={<DoudouNew />} />
      <Route path="/doudou/:id" element={<DoudouDetail />} />
      <Route path="/doudou/:id/modifier" element={<DoudouEdit />} />
      {/* FEATURE FLAG: Étiquettes QR — désactivé pour MVP
      <Route path="/doudou/:id/etiquettes" element={<OrderStickers />} />
      */}
      <Route path="/scan/:id" element={<ScanPage />} />
      <Route path="/alerte/:doudou_id" element={<AlertePublique />} />
      <Route path="/trouve" element={<FoundPage />} />
      <Route path="/doudous-trouves" element={<FoundReports />} />
      <Route path="/mes-messages" element={<FinderHub />} />
      <Route path="/paiement/success" element={<PaymentSuccess />} />
      <Route path="/conversations" element={<Conversations />} />
      <Route path="/conversations/:id" element={<Chat />} />
      <Route path="/finder/:token/conversation/:convId" element={<FinderConversationRedirect />} />
      <Route path="/profil" element={<Profile />} />
      <Route path="/relais/:convId" element={<RelaisPage />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/confidentialite" element={<Privacy />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/a-propos" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
    </Routes>
    </PasswordGate>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppRouter />
        <CookieBanner />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
